import { useAppSelector } from 'common/hooks/redux'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import {
  useFetchProceduresQuery,
  useFetchSoapNotesQuery,
  useDeleteSoapImageMutation,
} from 'features/Procedure/state/procedure.api'
import type { ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { useParams } from 'react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { hasPendingSoapImages } from 'features/Procedure/state/soapImageUpload.helpers'
import { useUploadProcedureSoapImage } from '../../hooks/useUploadProcedureSoapImage'
import styles from './procedure-image-list.module.scss'
import { ProcedureImagePreview } from './procedure-image-preview'
import { ProcedureItemRow } from './procedure-image-row'

type Props = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
}

export const ProcedureImageList = ({
  selectedPatientId,
  selectedEncounterUid,
  disableActions = false,
}: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { setNotification } = useNotification()
  const { handleSetSelectedProcedure } = useProcedureActions()

  const [previewImage, setPreviewImage] = useState<string | null>(null)

  const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
  const isProcedureDeletePending = useAppSelector(
    (state) => state.procedureReducer.isProcedureDeletePending,
  )

  const { data: proceduresData } = useFetchProceduresQuery(
    { siteId: siteId!, encounterUid: selectedEncounterUid! },
    { skip: !siteId || !selectedEncounterUid },
  )

  const {
    currentData: soapNotesData,
    isFetching: isFetchingSoapNotes,
    isLoading: isLoadingSoapNotes,
  } = useFetchSoapNotesQuery(
    { patientPid: selectedPatientId!, encounterUid: selectedEncounterUid! },
    { skip: !selectedPatientId || !selectedEncounterUid },
  )

  const [deleteSoapImage, { isLoading: isDeletingImage }] = useDeleteSoapImageMutation()

  const { uploadImage } = useUploadProcedureSoapImage({
    siteId,
    selectedPatientId,
    selectedEncounterUid,
  })

  const [uploadingProcedureIds, setUploadingProcedureIds] = useState<Set<number>>(() => new Set())

  const procedures = useMemo(
    () => proceduresData?.items?.filter((item) => item.code_type === ProcedureTypeCode.CPT4) || [],
    [proceduresData],
  )

  const soapImages = useMemo(() => {
    if (!soapNotesData || soapNotesData.length === 0) return []
    const images = soapNotesData.flatMap((note) => note.soap_images || [])
    return images
  }, [soapNotesData])

  const hasPendingUploads = uploadingProcedureIds.size > 0 || hasPendingSoapImages(soapNotesData)

  const handleSelectProcedure = useCallback(
    (procedure: ProcedureItem) => {
      handleSetSelectedProcedure(procedure)
    },
    [handleSetSelectedProcedure],
  )

  const handleDeleteImage = useCallback(
    async (imageId: number) => {
      if (!selectedPatientId || !selectedEncounterUid) return

      try {
        await deleteSoapImage({
          patientPid: selectedPatientId,
          encounterUid: selectedEncounterUid,
          imageId,
        }).unwrap()
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Unable to delete image',
          description: error?.data?.message || 'Please try again later.',
        })
      }
    },
    [deleteSoapImage, selectedPatientId, selectedEncounterUid, setNotification],
  )

  const handleUploadPhoto = useCallback(
    async (procedure: ProcedureItem, dataUrl: string) => {
      setUploadingProcedureIds((prev) => {
        const next = new Set(prev)
        next.add(procedure.id)
        return next
      })
      try {
        await uploadImage({ procedure, imageDataUrl: dataUrl })
      } finally {
        setUploadingProcedureIds((prev) => {
          const next = new Set(prev)
          next.delete(procedure.id)
          return next
        })
      }
    },
    [uploadImage],
  )

  const handleRowUploadPhoto = useCallback(
    (procedure: ProcedureItem, dataUrl: string) => {
      void handleUploadPhoto(procedure, dataUrl)
    },
    [handleUploadPhoto],
  )

  useEffect(() => {
    if (disableActions || isProcedureDeletePending) {
      return
    }

    if (!selectedProcedure && procedures?.length > 0) {
      handleSelectProcedure(procedures[0])
    }
  }, [
    disableActions,
    isProcedureDeletePending,
    selectedProcedure,
    procedures,
    handleSelectProcedure,
  ])

  const getSoapImages = (procedureId: number) => {
    return soapImages?.filter((img) => img?.procedure_id === procedureId) || []
  }

  if (procedures.length === 0 && !isFetchingSoapNotes) {
    return (
      <div className={styles.emptyState}>
        <p>No procedures available</p>
      </div>
    )
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.title}>Procedures</div>
        <div className={styles.list}>
          {procedures.map((procedure, index) => (
            <ProcedureItemRow
              key={procedure.id}
              procedure={procedure}
              index={index}
              isSelected={selectedProcedure?.id === procedure.id}
              onSelect={() => handleSelectProcedure(procedure)}
              images={getSoapImages(procedure.id)}
              onDeleteImage={handleDeleteImage}
              isDeletingImage={isDeletingImage}
              onPreviewImage={setPreviewImage}
              onUploadPhoto={handleRowUploadPhoto}
              isUploadingPhoto={uploadingProcedureIds.has(procedure.id)}
              disableCapture={hasPendingUploads}
              isLoadingImage={isLoadingSoapNotes && soapImages.length === 0}
              disableActions={disableActions}
            />
          ))}
        </div>
      </div>
      <ProcedureImagePreview
        isOpen={!!previewImage}
        imageSrc={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </>
  )
}
